import * as React from 'react';
import Axios from 'axios';

import { ILogoStateInfo, ILogoVersionInfo } from '../../shared/logo.types';
import { ISimpleResponse } from '../../shared/common-dtos';
import { ILogoStateUpdateRequest } from '../../shared/components/preview-logo.component';
import { LoadingCircle } from 'src/App/shared/components/loading-circle.component';
import { DebuggerInstance } from 'src/App/shared/debugger';
import DtpConfirmLogoComponent from '../dtp-confirm-logo.component';

export interface IDtpStepLogoReviewProps {
    logoInfo: ILogoStateInfo;
    hash: string;
    baseApiUrl: string;
    onConfirmationChanged(logoVersion: ILogoVersionInfo, value: boolean): void;
    onLogoLoaded(logoVersion: ILogoVersionInfo, success: boolean): void;
    onDtpCompleted(): void;
    onRepeatUpload(): void;
    onRejectLogo(): void;
    onUpdateImages(): void;
}

export interface IDtpLogoReviewState {
    sendingRequest: boolean;
    replacingVariant: boolean;
    requestFailed: boolean;
}

const overlayLogoWrong = 'Logo is wrong and can not be used!'
const overlayVariantWrong = 'This variant is wrong and can not be used!'

class DtpStepLogoReviewComponent extends React.PureComponent<IDtpStepLogoReviewProps,IDtpLogoReviewState>{
    constructor(props: IDtpStepLogoReviewProps) {
        super(props);

        this.state = {
            sendingRequest: false,
            replacingVariant: false,
            requestFailed: false,
        };
    }

    private async handleAccept() {
        this.setState({
                sendingRequest: true
            });

        try {
            const logosState = this.props.logoInfo;
            const request: ILogoStateUpdateRequest = {
                normal: logosState.normal.accept,
                white: logosState.white.accept,
                black: logosState.black.accept
            }

            const apiUrl = this.props.baseApiUrl + `logo-accept/${this.props.logoInfo.logoId}/${this.props.hash}`;
            const responseData = await Axios.post(apiUrl, request);
            const response: ISimpleResponse = responseData.data as ISimpleResponse;

            if (response && response.success) {
                this.setState({
                    sendingRequest: false
                });
                this.props.onDtpCompleted();
            } else {
                DebuggerInstance.error('DtpStepLogoReview.handleAccept - request failed with response:')
                DebuggerInstance.error(response);

                this.setState({
                    sendingRequest: false,
                    requestFailed: true
                });
            }
        } catch (error) {
            DebuggerInstance.error('DtpStepLogoReview.handleAccept - failed with error:')
            DebuggerInstance.error(error);
            this.setState({
                sendingRequest: false,
                requestFailed: true
            });
        }
    }

    private uploadFile(file: File, method: string, url: string): void {
        this.setState({
            replacingVariant: true
        });

        const formData = new FormData();
        formData.append('logoFile', file, file.name);

        const request = new XMLHttpRequest();
        request.open(method, url);

        // request.upload.onprogress = e => this.handleUploadProgress(e);

        request.onload = () => {
            this.handleUploadCompleted(request.status, request.responseText);
        };

        request.onerror = () => {
            this.setState({
                replacingVariant: false
            });
        }

        request.send(formData);
    }

    private handleUploadCompleted(status: number, responseText: string) {
        DebuggerInstance.log('handleUploadCompleted');
        DebuggerInstance.log(status);
        DebuggerInstance.log(responseText);

        this.props.onUpdateImages();

        this.setState({
            replacingVariant: false
        });
    }

    private getVariantPreviews() {
        const previews: JSX.Element[] = [];
        const logo = this.props.logoInfo;

        // Original colors
        let preview = (
            <div className="col-lg-4" key={'preview-original'}>
                <DtpConfirmLogoComponent
                    logoVersion={logo.normal}
                    useDarkBackground={false}
                    disableButtons={this.state.sendingRequest || this.state.replacingVariant}
                    showNotAcceptedOverlay={!logo.normal.accept}
                    notAcceptedText={overlayLogoWrong}
                    onConfirmChanged={(id, value) => this.props.onConfirmationChanged(id, value)}
                    onLoadComplete={(id, success) => this.props.onLogoLoaded(id, success)}
                    onFileDrop={(variant, file) => {
                        this.uploadFile(file, 'PUT', `${this.props.baseApiUrl}reupload/${logo.logoId}/${this.props.hash}?variant=${variant}`)
                    }}
                />
            </div>
        );
        previews.push(preview);

        // White colors
        preview = (
            <div className="col-lg-4" key={'preview-white'}>
                <DtpConfirmLogoComponent
                    logoVersion={logo.white}
                    useDarkBackground={true}
                    disableButtons={!logo.normal.accept || this.state.replacingVariant}
                    showNotAcceptedOverlay={!(logo.white.accept && logo.normal.accept)}
                    notAcceptedText={logo.normal.accept ? overlayVariantWrong : overlayLogoWrong}
                    onConfirmChanged={(id, value) => this.props.onConfirmationChanged(id, value)}
                    onLoadComplete={(id, success) => this.props.onLogoLoaded(id, success)}
                    onFileDrop={(variant, file) => {
                        this.uploadFile(file, 'PUT', `${this.props.baseApiUrl}reupload/${logo.logoId}/${this.props.hash}?variant=${variant}`)
                    }}
                />
            </div>
        );
        previews.push(preview);

        // Black colors
        preview = (
            <div className="col-lg-4" key={'preview-black'}>
                <DtpConfirmLogoComponent
                    logoVersion={logo.black}
                    useDarkBackground={false}
                    disableButtons={!logo.normal.accept || this.state.replacingVariant}
                    showNotAcceptedOverlay={!(logo.black.accept && logo.normal.accept)}
                    notAcceptedText={logo.normal.accept ? overlayVariantWrong : overlayLogoWrong}
                    onConfirmChanged={(id, value) => this.props.onConfirmationChanged(id, value)}
                    onLoadComplete={(id, success) => this.props.onLogoLoaded(id, success)}
                    onFileDrop={(variant, file) => {
                        this.uploadFile(file, 'PUT', `${this.props.baseApiUrl}reupload/${logo.logoId}/${this.props.hash}?variant=${variant}`)
                    }}
                />
            </div>
        );
        previews.push(preview);

        return previews;
    }

    private getPreviews() {
        const previews = this.getVariantPreviews();

        return (

            <div className="container">
                <div className="row row-info">
                    <div className="col">
                        <p>
                            Please check processed uploaded PNG and automatically generated WHITE and BLACK variants and just switch OK/WRONG and finally confirm by
                            submit button (only confirm will activate logo in system and send notification to user).
                        </p>
                    </div>
                </div>
                <div className="row row-previews">{previews}</div>
                {this.state.replacingVariant &&
                    <div className="row row-replacing-variant">
                        <div className="replacing-loader">
                            <LoadingCircle />
                            <div className="ml-3 mb-3">Processing request...</div>
                        </div>
                    </div>
                }
            </div>);
    }

    private getActionButtons(): JSX.Element[] {
        if (this.state.sendingRequest) {
            return [];
        }

        const buttons: JSX.Element[] = [];
        buttons.push(
            <button className="action-continue"
                disabled={this.state.replacingVariant}
                key="repeat-logo-upload-btn"
                onClick={() => this.props.onRepeatUpload()}>Repeat logo upload</button>);

        if (this.props.logoInfo.normal.accept) {
            buttons.push(
                <button className="action-success"
                    disabled={this.state.replacingVariant} 
                    key="complete-request-btn"
                    onClick={() => this.handleAccept()}>Confirm logo &amp; Complete request</button>
            )
        } else {
            buttons.push(
                <button className="action-fail" 
                    disabled={this.state.replacingVariant}
                    key="reject"
                    onClick={() => this.props.onRejectLogo()}>Reject logo</button>
            )
        }

        return buttons;
    }

    public render() {
        return (
            <div className={'dtp-portal-card'}>
                <div className="title">
                    <span>Step: Review upload logo</span>
                </div>
                <div className="content">
                    <div className="dpt-logo-review-component-root">
                        {!this.state.sendingRequest && this.getPreviews()}
                        {this.state.sendingRequest && (
                            <div className="sending-request">
                                <LoadingCircle />
                                <div>Processing request ...</div>
                            </div>
                        )}
                        {this.state.requestFailed && 
                            <div className="error-message">
                                Failed to complete request due to server error!
                            </div>
                        }
                    </div>
                </div>
                <div className="actions">
                    {this.getActionButtons()}
                </div>
            </div>
        );
    }
}

export const DtpStepLogoReview = (props: IDtpStepLogoReviewProps) => <DtpStepLogoReviewComponent {...props} />



// WEBPACK FOOTER //
// ./src/App/dtp-portal/cards/dtp-step-logo-review.component.tsx